import React from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  Navigate,
  useRouterState,
} from '@tanstack/react-router';
import type { RouterHistory } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { AppShell } from '@/shared/components/layout/AppShell';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';
import { AccountHealthBootstrap } from './AccountHealthBootstrap';
import { useAuthStore } from '@/shared/stores/auth-store';
import { isEmbedded } from '@/shared/config/embedded-context';
import { PromotePermission } from '@/shared/types/permissions';
import { EmbeddedAuthGate } from './EmbeddedAuthGate';
import { NotFoundPage } from './NotFoundPage';
import { UnauthorizedPage } from './UnauthorizedPage';

// ---- Permission guard helpers ----

/**
 * Checks whether the current user has ALL of the given permissions.
 * If no session exists, skips the check (auth redirect is handled elsewhere).
 * If session exists but permissions are missing, redirects to /promote/unauthorized.
 * Designed for use inside TanStack Router `beforeLoad` hooks.
 */
function requirePermissions(...required: PromotePermission[]) {
  const { user, permissions } = useAuthStore.getState();

  // No session yet — let AuthBootstrap handle the redirect to login
  if (!user) return;

  const hasAll = required.every((p) => permissions.includes(p));
  if (!hasAll) {
    throw redirect({ to: '/promote/unauthorized' });
  }
}

// ---- Lazy page imports ----
const PromoteDashboardPage = React.lazy(() =>
  import('@/features/analytics').then((m) => ({ default: m.AnalyticsDashboardPage })),
);
const AccountsPage = React.lazy(() =>
  import('@/features/accounts').then((m) => ({ default: m.AccountsPage })),
);
const OAuthCallbackPage = React.lazy(() =>
  import('@/features/accounts').then((m) => ({ default: m.OAuthCallbackPage })),
);
const CampaignListPage = React.lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.CampaignListPage })),
);
const CampaignWizardPage = React.lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.CampaignWizardPage })),
);
const CampaignDetailPage = React.lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.CampaignDetailPage })),
);
const CampaignEditPage = React.lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.CampaignEditPage })),
);
const CampaignCheckoutPage = React.lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.CampaignCheckoutPage })),
);
const AnalyticsDashboardPage = React.lazy(() =>
  import('@/features/analytics').then((m) => ({ default: m.AnalyticsDashboardPage })),
);
const NotificationsPage = React.lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.NotificationsPage })),
);
const SettingsPage = React.lazy(() =>
  import('@/features/accounts').then((m) => ({ default: m.SettingsPage })),
);
const LoginPage = React.lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.LoginPage })),
);
const OrganizationSelectPage = React.lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.OrganizationSelectPage })),
);
const SSOCallbackPage = React.lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.SSOCallbackPage })),
);
const CreateOrganizationPage = React.lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.CreateOrganizationPage })),
);

function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoadingSession = useAuthStore((state) => state.isLoadingSession);
  const sessionHydrated = useAuthStore((state) => state.sessionHydrated);
  const orgId = useAuthStore((state) => state.orgId);
  const organizations = useAuthStore((state) => state.organizations);
  const content = <Outlet />;
  const isAuthRoute =
    pathname === '/login' ||
    pathname === '/select-organization' ||
    pathname === '/create-organization' ||
    pathname.startsWith('/auth/sso/');

  if (!sessionHydrated || isLoadingSession) {
    return <LoadingSpinner size="lg" label="Loading session..." />;
  }

  // Embedded (Module Federation): the host (sell-aui) provides the chrome and
  // the Promote nav, so render bare (no Promote AppShell). Authenticate off the
  // host's session via the gate instead of Promote's own login / org pages.
  const embedded = isEmbedded();

  if (embedded) {
    return isAuthenticated ? content : <EmbeddedAuthGate />;
  }

  if (!isAuthenticated) {
    return isAuthRoute ? content : <Navigate to="/login" />;
  }

  // Authenticated but no organizations -- need to create one (unless SSO sync is in progress)
  if (organizations.length === 0 && !pathname.startsWith('/auth/sso/')) {
    return pathname === '/create-organization' ? content : <Navigate to="/create-organization" />;
  }

  if (!orgId) {
    return pathname === '/select-organization' ? content : <Navigate to="/select-organization" />;
  }

  if (isAuthRoute) {
    return <Navigate to="/promote" />;
  }

  if (pathname.startsWith('/promote')) {
    return (
      <>
        <AccountHealthBootstrap />
        <AppShell>{content}</AppShell>
      </>
    );
  }

  return content;
}

// ---- Root route ----
const rootRoute = createRootRoute({
  component: () => (
    <React.Suspense fallback={<LoadingSpinner size="lg" />}>
      <RootLayout />
    </React.Suspense>
  ),
  notFoundComponent: NotFoundPage,
});

// ---- / -> redirect to /promote ----
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/promote' });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const organizationSelectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/select-organization',
  component: OrganizationSelectPage,
});

const createOrganizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/create-organization',
  component: CreateOrganizationPage,
});

const ssoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/sso/callback',
  component: SSOCallbackPage,
});

// ---- /promote/unauthorized ----
const unauthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/unauthorized',
  component: UnauthorizedPage,
});

// ---- /promote (Dashboard) -- requires CAMPAIGNS_VIEW ----
const promoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote',
  beforeLoad: () => {
    requirePermissions(PromotePermission.CAMPAIGNS_VIEW);
  },
  component: PromoteDashboardPage,
});

// ---- /promote/accounts -- requires ACCOUNTS_VIEW ----
const accountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/accounts',
  beforeLoad: () => {
    requirePermissions(PromotePermission.ACCOUNTS_VIEW);
  },
  component: AccountsPage,
});

// ---- /promote/accounts/connect/$platform -- requires ACCOUNTS_MANAGE ----
const accountsConnectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/accounts/connect/$platform',
  beforeLoad: () => {
    requirePermissions(PromotePermission.ACCOUNTS_MANAGE);
  },
  component: OAuthCallbackPage,
});

// ---- /promote/campaigns -- requires CAMPAIGNS_VIEW ----
const campaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/campaigns',
  beforeLoad: () => {
    requirePermissions(PromotePermission.CAMPAIGNS_VIEW);
  },
  component: CampaignListPage,
});

// ---- /promote/campaigns/new -- requires CAMPAIGNS_CREATE ----
const campaignsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/campaigns/new',
  beforeLoad: () => {
    requirePermissions(PromotePermission.CAMPAIGNS_CREATE);
  },
  component: CampaignWizardPage,
});

// ---- /promote/campaigns/$id -- requires CAMPAIGNS_VIEW ----
const campaignDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/campaigns/$id',
  beforeLoad: () => {
    requirePermissions(PromotePermission.CAMPAIGNS_VIEW);
  },
  component: CampaignDetailPage,
});

// ---- /promote/campaigns/$id/edit -- requires CAMPAIGNS_EDIT ----
const campaignEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/campaigns/$id/edit',
  beforeLoad: () => {
    requirePermissions(PromotePermission.CAMPAIGNS_EDIT);
  },
  component: CampaignEditPage,
});

// ---- /promote/campaigns/$id/checkout -- requires CAMPAIGNS_EDIT ----
// Isolated Stripe checkout. Not linked from the wizard or launch flow; the
// page itself renders "not available" unless VITE_STRIPE_PUBLISHABLE_KEY is set.
const campaignCheckoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/campaigns/$id/checkout',
  beforeLoad: () => {
    requirePermissions(PromotePermission.CAMPAIGNS_EDIT);
  },
  component: CampaignCheckoutPage,
});

// ---- /promote/analytics -- requires ANALYTICS_VIEW ----
const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/analytics',
  beforeLoad: () => {
    requirePermissions(PromotePermission.ANALYTICS_VIEW);
  },
  component: AnalyticsDashboardPage,
});

// ---- /promote/notifications -- requires CAMPAIGNS_VIEW ----
const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/notifications',
  beforeLoad: () => {
    requirePermissions(PromotePermission.CAMPAIGNS_VIEW);
  },
  component: NotificationsPage,
});

// ---- /promote/settings -- requires SETTINGS_VIEW ----
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promote/settings',
  beforeLoad: () => {
    requirePermissions(PromotePermission.SETTINGS_VIEW);
  },
  component: SettingsPage,
});

// ---- Route tree ----
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  organizationSelectRoute,
  createOrganizationRoute,
  ssoCallbackRoute,
  unauthorizedRoute,
  promoteRoute,
  accountsRoute,
  accountsConnectRoute,
  campaignsRoute,
  campaignsNewRoute,
  campaignDetailRoute,
  campaignEditRoute,
  campaignCheckoutRoute,
  analyticsRoute,
  notificationsRoute,
  settingsRoute,
]);

export interface CreateAppRouterOptions {
  /**
   * Custom router history. Standalone omits it (browser history); the Module
   * Federation remote passes a memory history so it never owns the host URL.
   */
  history?: RouterHistory;
}

/** Creates the TanStack Router instance. */
export function createAppRouter(
  _queryClient: QueryClient,
  { history }: CreateAppRouterOptions = {},
) {
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    ...(history ? { history } : {}),
    context: {
      queryClient: _queryClient,
    },
  });
}

// Type registration for TanStack Router
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
